import { useContext, useEffect } from "react";
import { Field } from "formik";
import PropTypes from "prop-types";
import { createUseStyles } from "react-jss";

import DatePicker, { registerLocale } from "react-datepicker";
import enUS from "date-fns/locale/en-US";
import el from "date-fns/locale/el";
import fr from "date-fns/locale/fr";
import zhCN from "date-fns/locale/zh-CN";
import ja from "date-fns/locale/ja";
import de from "date-fns/locale/de";
import es from "date-fns/locale/es";
import it from "date-fns/locale/it";
import pt from "date-fns/locale/pt";
import th from "date-fns/locale/th";
import { DateTime } from "luxon";
import "react-datepicker/dist/react-datepicker.css";
import "react-datepicker/dist/react-datepicker-cssmodules.css";
import { commonInputGroupStyles } from "..";
import { TranslationContext } from "../../../../umbilical_tools";
import jssVariables from "../../../../jss_setup";
import { useTranslation } from "react-i18next";

registerLocale("enUS", enUS);
registerLocale("el", el);
registerLocale("fr", fr);
registerLocale("ja", ja);
registerLocale("zhCN", zhCN);
registerLocale("de", de);
registerLocale("es", es);
registerLocale("it", it);
registerLocale("pt", pt);
registerLocale("th", th);

const localeLanguageMapping = {
  ZH: "zhCN",
  EN: "enUS",
  FR: "fr",
  EL: "el",
  ES: "es",
  DE: "de",
  IT: "it",
  PT: "pt",
  JA: "ja",
  TH: "th",
};

const datepickerFieldStyles = createUseStyles({
  inputGroup: (props) => ({
    ...commonInputGroupStyles.inputGroup,
    ...(props?.customStyles?.inputGroup ?? {}),
  }),
  label: (props) => ({
    ...commonInputGroupStyles.label,
    ...(props?.extraStyles?.label ?? {}),
    ...(props?.customStyles?.label ?? {}),
  }),
  requiredLabel: (props) => ({
    fontWeight: "normal",
    ...(props?.customStyles?.requiredLabel ?? {}),
  }),
  errorMessage: (props) => ({
    ...commonInputGroupStyles.errorMessage,
    ...(props?.customStyles?.errorMessage ?? {}),
  }),
  datepicker: (props) => ({
    width: "100%",
    padding: jssVariables.normal_gap,
    fontFamily: jssVariables.fontFamily,
    borderRadius: "5px",
    fontSize: "medium",
    height: "3rem",
    color: jssVariables.colors.text.dark,
    border: `1px solid ${jssVariables.colors.border}`,
    "&:disabled": { background: "lightgray" },
    ...(props?.customStyles?.datepicker ?? {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    inputGroup: (props) => ({
      ...(props?.customStyles?.inputGroup?.smallscreen ?? {}),
    }),
  },
});
function CustomDatepickerComponent({ field, form: { setFieldValue }, ...props }) {
  const { name } = field;

  let {
    minDate,
    maxDate,
    showYearDropdown,
    yearDropdownItemsNum,
    customStyles,
    selectsRange,
    portalId,
    dateFormat,
    placeholder,
    filterDate,
    showTimeSelect,
    isDisabled,
  } = props;

  useEffect(() => {
    if (filterDate && !filterDate(DateTime.fromISO(field.value).toJSDate())) {
      setFieldValue(
        name,
        showTimeSelect
          ? DateTime.fromISO(field.value).plus({ days: 1 }).toISO()
          : DateTime.fromISO(field.value).plus({ days: 1 }).toISODate(),
      );
    }
  }, [field.value]);

  function onUpdate(date: Date) {
    let [start, end] = [{}, {}];

    if (selectsRange) {
      [start, end] = date;
    } else {
      start = date;
    }

    start = showTimeSelect
      ? DateTime.fromJSDate(start).toISO()
      : DateTime.fromJSDate(start).toISODate();
    if (selectsRange) {
      end = DateTime.fromJSDate(end).toISODate();
      setFieldValue(name, [start, end]);
    } else {
      setFieldValue(name, start);
    }
  }
  const selectedDatesProps = {};

  if (minDate) {
    minDate = DateTime.fromISO(minDate).toJSDate();
  }
  if (maxDate) {
    maxDate = DateTime.fromISO(maxDate).toJSDate();
  }

  if (selectsRange) {
    selectedDatesProps["startDate"] = field.value[0]
      ? DateTime.fromISO(field.value[0]).toJSDate()
      : null;
    selectedDatesProps["endDate"] = field.value[1]
      ? DateTime.fromISO(field.value[1]).toJSDate()
      : field.value[1];
  } else {
    let val = field.value;
    if (Array.isArray(val)) {
      val = val[0];
    }

    selectedDatesProps["selected"] = val ? DateTime.fromISO(val).toJSDate() : null;
  }

  const { i18n } = useTranslation();

  return (
    <DatePicker
      className={props.styles.datepicker}
      minDate={minDate}
      maxDate={maxDate}
      onChange={onUpdate}
      withPortal={true}
      showTimeSelect={showTimeSelect}
      customStyles={customStyles}
      selectsRange={selectsRange}
      portalId={portalId}
      dateFormat={dateFormat}
      placeholderText={placeholder}
      showYearDropdown={showYearDropdown}
      yearDropdownItemNumber={yearDropdownItemsNum}
      scrollableYearDropdown={true}
      filterDate={filterDate}
      disabled={isDisabled}
      onFocus={(e) => e.target.blur()}
      locale={localeLanguageMapping[i18n.language]}
      {...selectedDatesProps}
    />
  );
}
CustomDatepickerComponent.propTypes = {
  minDate: PropTypes.string,
  maxDate: PropTypes.string,
  showYearDropdown: PropTypes.bool,
  yearDropdownItemsNum: PropTypes.number,
  customStyles: PropTypes.object,
  selectsRange: PropTypes.bool,
  portalId: PropTypes.string,
  dateFormat: PropTypes.string,
  placeholder: PropTypes.string,
  filterDate: PropTypes.func,
  showTimeSelect: PropTypes.bool,
  isDisabled: PropTypes.bool,
  field: PropTypes.object.isRequired,
  form: PropTypes.shape({
    setFieldValue: PropTypes.func.isRequired,
  }).isRequired,
};

export function DatepickerField({
  id,
  name,
  label,
  placeholder,
  withPortal,
  portalId,
  dateFormat,
  customStyles,
  selectsRange,
  showYearDropdown,
  yearDropdownItemsNum,
  minDate,
  maxDate,
  filterDate,
  isRequired,
  showTimeSelect,
  isDisabled,
}) {
  const classes = datepickerFieldStyles({ customStyles });
  const { t } = useContext(TranslationContext) as { t: (key: string) => string };

  return (
    <div className={`${classes.inputGroup} [&>div>div>input]:bg-white`} id={id}>
      <label className={classes.label}>
        {t(label)}
        {isRequired && <span className={classes.requiredLabel}>{t("required")}</span>}
      </label>
      <Field
        name={name}
        styles={classes}
        placeholder={placeholder}
        withPortal={withPortal}
        portalId={portalId}
        dateFormat={dateFormat}
        minDate={minDate}
        maxDate={maxDate}
        selectsRange={selectsRange}
        customStyles={customStyles}
        showYearDropdown={showYearDropdown}
        yearDropdownItemsNum={yearDropdownItemsNum}
        filterDate={filterDate}
        showTimeSelect={showTimeSelect}
        isDisabled={isDisabled}
        component={CustomDatepickerComponent}
      />
    </div>
  );
}
DatepickerField.defaultProps = {
  id: "",
  label: "",
  as: "select",
  placeholder: "",
  disabled: false,
  withPortal: true,
  portalId: "root-portal",
  minDate: "",
  maxDate: "",
  selectsRange: false,
  popperPlacement: "top-end",
  dateFormat: "MMM d, yyyy",
  showYearDropdown: false,
  yearDropdownItemsNum: 5,
  showTimeSelect: false,
  isDisabled: false,
};
DatepickerField.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string.isRequired,
  customStyles: PropTypes.object,
  isRequired: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  withPortal: PropTypes.bool,
  portalId: PropTypes.string,
  dateFormat: PropTypes.string,
  minDate: PropTypes.string,
  maxDate: PropTypes.string,
  startDate: PropTypes.object,
  endDate: PropTypes.object,
  selectsRange: PropTypes.bool,
  popperPlacement: PropTypes.string,
  showYearDropdown: PropTypes.bool,
  yearDropdownItemsNum: PropTypes.number,
  showTimeSelect: PropTypes.bool,
  isDisabled: PropTypes.bool,
  filterDate: PropTypes.func,
};
